import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";

export type Theme = "light" | "dark" | "system";
export type Contrast = "normal" | "more" | "system";
export type Motion = "full" | "reduced" | "system";
export type FontScale = "1" | "1.125" | "1.25" | "1.5";

const THEMES: Theme[] = ["light", "dark", "system"];
const CONTRASTS: Contrast[] = ["normal", "more", "system"];
const MOTIONS: Motion[] = ["full", "reduced", "system"];
const FONT_SCALES: FontScale[] = ["1", "1.125", "1.25", "1.5"];

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  "1": "Default",
  "1.125": "Large",
  "1.25": "Larger",
  "1.5": "Largest",
};

interface AppearanceContextValue {
  theme: Theme;
  setTheme: (value: Theme) => void;
  resolvedTheme: "light" | "dark";
  contrast: Contrast;
  setContrast: (value: Contrast) => void;
  resolvedContrast: "normal" | "more";
  motion: Motion;
  setMotion: (value: Motion) => void;
  resolvedMotion: "full" | "reduced";
  fontScale: FontScale;
  setFontScale: (value: FontScale) => void;
}

const AppearanceContext = createContext<AppearanceContextValue | undefined>(
  undefined,
);

/**
 * Subscribes to a media query. Replaces the hand-rolled matchMedia listeners
 * that previously re-derived and re-applied classes themselves — with four
 * settings that duplication would have tripled.
 */
function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mq = window.matchMedia(query);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

function readStored<T extends string>(key: string, valid: T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return valid.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    // Blocked storage (Safari private mode) must not break appearance.
    return fallback;
  }
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

export function AppearanceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [theme, setThemeState] = useState<Theme>(() =>
    readStored("theme", THEMES, "system"),
  );
  const [contrast, setContrastState] = useState<Contrast>(() =>
    readStored("contrast", CONTRASTS, "system"),
  );
  const [motion, setMotionState] = useState<Motion>(() =>
    readStored("motion", MOTIONS, "system"),
  );
  const [fontScale, setFontScaleState] = useState<FontScale>(() =>
    readStored("fontScale", FONT_SCALES, "1"),
  );

  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");
  const systemContrast = useMediaQuery("(prefers-contrast: more)");
  const systemReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Contrast is an axis of its own rather than a fourth theme value: needing
  // maximum contrast and preferring a dark UI are independent, and forcing one
  // to imply the other is itself an accessibility failure.
  const resolvedTheme =
    theme === "system" ? (systemDark ? "dark" : "light") : theme;
  const resolvedContrast =
    contrast === "system" ? (systemContrast ? "more" : "normal") : contrast;
  const resolvedMotion =
    motion === "system" ? (systemReducedMotion ? "reduced" : "full") : motion;

  // One effect owns every class, so the resolution logic exists in one place.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
    root.classList.toggle("hc", resolvedContrast === "more");
    root.classList.toggle("reduce-motion", resolvedMotion === "reduced");
    root.style.setProperty("--font-scale", fontScale);
  }, [resolvedTheme, resolvedContrast, resolvedMotion, fontScale]);

  const setTheme = useCallback((value: Theme) => {
    setThemeState(value);
    persist("theme", value);
  }, []);
  const setContrast = useCallback((value: Contrast) => {
    setContrastState(value);
    persist("contrast", value);
  }, []);
  const setMotion = useCallback((value: Motion) => {
    setMotionState(value);
    persist("motion", value);
  }, []);
  const setFontScale = useCallback((value: FontScale) => {
    setFontScaleState(value);
    persist("fontScale", value);
  }, []);

  return (
    <AppearanceContext.Provider
      value={{
        theme,
        setTheme,
        resolvedTheme,
        contrast,
        setContrast,
        resolvedContrast,
        motion,
        setMotion,
        resolvedMotion,
        fontScale,
        setFontScale,
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error("useAppearance must be used within an AppearanceProvider");
  }
  return context;
}
